// Land Intelligence Filter Panel — Brian's Ellis Deep Scan Task 3 (2026-05-09).
//
// Filter controls + results (table or map). All filters AND-combined; results
// debounced 300ms. Save Filter persists to land_filter_presets via the
// backend's CRUD endpoints. Map mode reuses LandMap's CONUS SVG projection
// pattern for a lightweight overview without adding leaflet/mapbox deps.
//
// Brian's spec: "sit down with the map, set filters, find every parcel in
// Ellis County that looks like Red Oak — without writing SQL, without
// waiting for Sunday."
//
// Brain: [skills/land-intelligence].
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  MapContainer, TileLayer, CircleMarker, Marker, Popup, Polyline,
  LayersControl, useMap,
} from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

const API_BASE = process.env.REACT_APP_API_URL || '';

const CONUS = {
  lat_min: 24.5, lat_max: 49.5,
  lon_min: -125.0, lon_max: -66.5,
};

const SVG_WIDTH = 1100;
const SVG_HEIGHT = 600;

function project(latitude, longitude) {
  const x = ((longitude - CONUS.lon_min) / (CONUS.lon_max - CONUS.lon_min)) * SVG_WIDTH;
  const y = ((CONUS.lat_max - latitude) / (CONUS.lat_max - CONUS.lat_min)) * SVG_HEIGHT;
  return { x, y };
}

// Reference cities give the map orientation. Without them the SVG is
// just dots floating in gray space — Brian's "essentially blank" report.
const REFERENCE_CITIES = [
  { name: 'Dallas',     lat: 32.78, lon: -96.80 },
  { name: 'Houston',    lat: 29.76, lon: -95.37 },
  { name: 'Austin',     lat: 30.27, lon: -97.74 },
  { name: 'Atlanta',    lat: 33.75, lon: -84.39 },
  { name: 'Nashville',  lat: 36.13, lon: -86.68 },
  { name: 'Phoenix',    lat: 33.45, lon: -112.07 },
  { name: 'Chicago',    lat: 41.88, lon: -87.63 },
  { name: 'Denver',     lat: 39.74, lon: -104.99 },
  { name: 'Charlotte',  lat: 35.23, lon: -80.84 },
  { name: 'NoVA/DC',    lat: 38.91, lon: -77.04 },
  { name: 'Memphis',    lat: 35.15, lon: -90.05 },
  { name: 'Birmingham', lat: 33.52, lon: -86.81 },
  { name: 'Columbus',   lat: 39.96, lon: -82.99 },
];

// Auto-fit viewBox to the result bounding box. When Brian filters to
// Ellis County TX, the map zooms into Texas instead of showing all of
// CONUS with the cluster squished into 5% of the canvas.
function fitViewBoxToResults(results) {
  const valid = results.filter((r) => r.latitude != null && r.longitude != null);
  if (valid.length === 0) {
    return `0 0 ${SVG_WIDTH} ${SVG_HEIGHT}`;
  }
  // Compute bbox in lat/lon, project, then add padding.
  let minLat = Infinity, maxLat = -Infinity, minLon = Infinity, maxLon = -Infinity;
  for (const r of valid) {
    if (r.latitude < minLat) minLat = r.latitude;
    if (r.latitude > maxLat) maxLat = r.latitude;
    if (r.longitude < minLon) minLon = r.longitude;
    if (r.longitude > maxLon) maxLon = r.longitude;
  }
  // Expand ±0.5° (≈35 mi) so a single-point cluster shows context, plus
  // expand further for tight clusters so we don't over-zoom.
  const latRange = Math.max(maxLat - minLat, 1.5);
  const lonRange = Math.max(maxLon - minLon, 1.5);
  const padLat = latRange * 0.35;
  const padLon = lonRange * 0.35;
  const padded = {
    minLat: Math.max(CONUS.lat_min, minLat - padLat),
    maxLat: Math.min(CONUS.lat_max, maxLat + padLat),
    minLon: Math.max(CONUS.lon_min, minLon - padLon),
    maxLon: Math.min(CONUS.lon_max, maxLon + padLon),
  };
  const tl = project(padded.maxLat, padded.minLon);
  const br = project(padded.minLat, padded.maxLon);
  const x = Math.max(0, tl.x);
  const y = Math.max(0, tl.y);
  const w = Math.min(SVG_WIDTH, br.x) - x;
  const h = Math.min(SVG_HEIGHT, br.y) - y;
  if (w <= 0 || h <= 0) return `0 0 ${SVG_WIDTH} ${SVG_HEIGHT}`;
  return `${x} ${y} ${w} ${h}`;
}

function authHeaders() {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

const COUNTIES_DEFAULT = [
  'Maury', 'Williamson', 'Bedford', 'Rutherford', 'Marshall',  // TN
  'Williamson', 'Ellis', 'Collin', 'Denton', 'Ector',           // TX
  'Douglas', 'Henry',                                            // GA
  'Licking',                                                     // OH
  'Madison',                                                     // AL
];
const COUNTIES_OPTIONS = Array.from(new Set(COUNTIES_DEFAULT));

const STATES_OPTIONS = ['TX', 'TN', 'GA', 'OH', 'AL', 'NC', 'VA', 'AZ'];

const OWNER_TYPES = ['LLC', 'INC', 'TRUST', 'INDIVIDUAL', 'CHURCH', 'GOV'];

const PIPELINE_STAGES = [
  'discovered', 'screening', 'qualified', 'diligence',
  'loi', 'contract', 'closed', 'passed',
];

const SCORE_COLORS = (score) => {
  if (score == null) return '#a1a1aa';      // zinc-400 — null/unknown
  if (score >= 60) return '#10b981';        // emerald-500 — high
  if (score >= 30) return '#f59e0b';        // amber-500 — medium
  return '#ef4444';                          // red-500 — low
};

const DEFAULT_FILTERS = {
  state: [],
  county: [],
  acreage_min: 80,
  acreage_max: 500,
  max_substation_mi: 5.0,
  transmission_on_property: null,
  max_highway_mi: 3.0,
  owner_types: ['LLC', 'INDIVIDUAL', 'TRUST'],
  exclude_gov_church: true,
  recent_transfer_months: 0,
  min_score: 0,
  pipeline_stage: [],
  zoning_contains: '',
  fiber_operator_signal: null,
  flood_zone_filter: 'all',  // 'all' | 'X_only' | 'X_or_A'
  sort_by: 'composite_score',
  sort_dir: 'desc',
  limit: 100,
};

// Map a FEMA flood zone string → CSS color for the parcel pin ring.
// Zone X / unknown → no ring; Zone A* → yellow; Zone V* → red.
function floodZoneRing(floodZone) {
  if (!floodZone) return null;
  const z = String(floodZone).trim().toUpperCase();
  if (z.startsWith('V')) return '#dc2626';      // red-600 — coastal high-velocity
  if (z.startsWith('A')) return '#eab308';      // yellow-500 — 100-yr floodplain
  return null;                                   // X / shaded-X / outside SFHA
}

export default function LandFilter() {
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [results, setResults] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [viewMode, setViewMode] = useState('table');
  const [presets, setPresets] = useState([]);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [presetName, setPresetName] = useState('');

  // Debounce
  const debounceRef = useRef(null);

  const buildQuery = useCallback((f) => {
    const params = new URLSearchParams();
    (f.state || []).forEach((s) => params.append('state', s));
    (f.county || []).forEach((c) => params.append('county', c));
    if (f.acreage_min != null) params.set('acreage_min', f.acreage_min);
    if (f.acreage_max != null) params.set('acreage_max', f.acreage_max);
    if (f.max_substation_mi != null) params.set('max_substation_mi', f.max_substation_mi);
    if (f.transmission_on_property === true) params.set('transmission_on_property', 'true');
    if (f.max_highway_mi != null) params.set('max_highway_mi', f.max_highway_mi);
    (f.owner_types || []).forEach((ot) => params.append('owner_types', ot));
    params.set('exclude_gov_church', f.exclude_gov_church ? 'true' : 'false');
    if (f.recent_transfer_months > 0) params.set('recent_transfer_months', f.recent_transfer_months);
    if (f.min_score > 0) params.set('min_score', f.min_score);
    (f.pipeline_stage || []).forEach((p) => params.append('pipeline_stage', p));
    if (f.zoning_contains) params.set('zoning_contains', f.zoning_contains);
    if (f.fiber_operator_signal === true) params.set('fiber_operator_signal', 'true');
    if (f.flood_zone_filter && f.flood_zone_filter !== 'all') {
      params.set('flood_zone_filter', f.flood_zone_filter);
    }
    params.set('sort_by', f.sort_by || 'composite_score');
    params.set('sort_dir', f.sort_dir || 'desc');
    params.set('limit', f.limit || 100);
    return params.toString();
  }, []);

  const runQuery = useCallback(async (f) => {
    setLoading(true);
    setError(null);
    try {
      const qs = buildQuery(f);
      const resp = await fetch(`${API_BASE}/land/parcels-search?${qs}`, {
        headers: authHeaders(),
      });
      if (!resp.ok) throw new Error(`filter: ${resp.status}`);
      const body = await resp.json();
      setResults(body.results || []);
      setTotal(body.total || 0);
    } catch (e) {
      setError(e.message || String(e));
      setResults([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [buildQuery]);

  // Debounced effect — 300ms
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => runQuery(filters), 300);
    return () => clearTimeout(debounceRef.current);
  }, [filters, runQuery]);

  // Load presets on mount
  useEffect(() => {
    fetch(`${API_BASE}/land/filter-presets`, { headers: authHeaders() })
      .then((r) => r.json())
      .then((b) => setPresets(b.presets || []))
      .catch(() => {});
  }, []);

  const updateFilter = (key, value) => setFilters((f) => ({ ...f, [key]: value }));

  const toggleListItem = (key, item) => setFilters((f) => {
    const current = f[key] || [];
    return { ...f, [key]: current.includes(item) ? current.filter((x) => x !== item) : [...current, item] };
  });

  const savePreset = async () => {
    if (!presetName.trim()) return;
    try {
      const resp = await fetch(`${API_BASE}/land/filter-presets`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({
          name: presetName.trim(),
          created_by: 'brian',
          filters,
        }),
      });
      if (!resp.ok) throw new Error(`save: ${resp.status}`);
      const body = await resp.json();
      setPresets((p) => [body, ...p]);
      setShowSaveModal(false);
      setPresetName('');
    } catch (e) {
      setError(e.message || String(e));
    }
  };

  const loadPreset = (preset) => {
    setFilters({ ...DEFAULT_FILTERS, ...(preset.filters || {}) });
  };

  const deletePreset = async (id) => {
    if (!window.confirm('Delete this filter preset?')) return;
    await fetch(`${API_BASE}/land/filter-presets/${id}`, {
      method: 'DELETE', headers: authHeaders(),
    });
    setPresets((p) => p.filter((x) => x.preset_id !== id));
  };

  return (
    <div className="min-h-screen bg-zinc-50 p-6">
      <div className="mx-auto max-w-7xl">
        <header className="mb-4 flex items-baseline justify-between">
          <div>
            <h1 className="text-2xl font-bold text-zinc-900">Land Filter</h1>
            <p className="text-sm text-zinc-500">
              Find parcels matching infrastructure + ownership criteria. All filters AND-combined.
            </p>
          </div>
          <div className="flex gap-2">
            <button onClick={() => setViewMode(viewMode === 'table' ? 'map' : 'table')}
                    className="rounded-lg border border-zinc-300 bg-white px-3 py-2 text-sm font-semibold hover:bg-zinc-100">
              {viewMode === 'table' ? '🗺️ Map view' : '📋 Table view'}
            </button>
            <button onClick={() => setShowSaveModal(true)}
                    className="rounded-lg border border-zinc-900 bg-zinc-900 px-3 py-2 text-sm font-semibold text-white hover:bg-zinc-800">
              💾 Save filter
            </button>
          </div>
        </header>

        {/* Saved presets */}
        {presets.length > 0 && (
          <div className="mb-4 flex flex-wrap items-center gap-2 rounded-xl border border-zinc-200 bg-white p-3">
            <span className="text-xs font-semibold uppercase tracking-wider text-zinc-500">Saved:</span>
            {presets.map((p) => (
              <span key={p.preset_id} className="group flex items-center gap-1 rounded-full border border-zinc-300 bg-zinc-50 px-3 py-1 text-xs">
                <button onClick={() => loadPreset(p)} className="font-semibold text-zinc-800 hover:text-zinc-900">
                  {p.name}
                </button>
                <button onClick={() => deletePreset(p.preset_id)} className="text-zinc-400 hover:text-red-600">×</button>
              </span>
            ))}
          </div>
        )}

        {/* Filter controls */}
        <FilterControls filters={filters} updateFilter={updateFilter} toggleListItem={toggleListItem} />

        {/* Status bar */}
        <div className="my-3 flex items-baseline justify-between text-sm">
          <span className="text-zinc-600">
            {loading ? 'Searching…' : `${total} parcel${total === 1 ? '' : 's'} match`}
            {results.length < total && ` (showing first ${results.length})`}
          </span>
          {error && <span className="text-red-700">{error}</span>}
        </div>

        {/* Results */}
        {viewMode === 'table' ? (
          <ResultsTable results={results} />
        ) : (
          <ResultsMap results={results} />
        )}

        {showSaveModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
            <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl">
              <h3 className="mb-3 text-lg font-bold text-zinc-900">Save filter preset</h3>
              <input value={presetName} onChange={(e) => setPresetName(e.target.value)}
                     placeholder='e.g. "DFW corridor Red Oak pattern"'
                     className="w-full rounded-lg border border-zinc-300 px-3 py-2 text-sm" autoFocus />
              <div className="mt-4 flex justify-end gap-2">
                <button onClick={() => setShowSaveModal(false)} className="rounded-lg border border-zinc-300 bg-white px-4 py-2 text-sm font-semibold">Cancel</button>
                <button onClick={savePreset} disabled={!presetName.trim()}
                        className="rounded-lg border border-zinc-900 bg-zinc-900 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50">Save</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function FilterControls({ filters, updateFilter, toggleListItem }) {
  return (
    <div className="grid grid-cols-1 gap-4 rounded-2xl border border-zinc-200 bg-white p-4 sm:grid-cols-2 lg:grid-cols-3">
      {/* State multi-select */}
      <div>
        <label className="text-xs font-semibold uppercase tracking-wider text-zinc-500">State</label>
        <div className="mt-1 flex flex-wrap gap-1">
          {STATES_OPTIONS.map((s) => (
            <button key={s} onClick={() => toggleListItem('state', s)}
                    className={`rounded-full px-3 py-1 text-xs font-semibold ${
                      (filters.state || []).includes(s)
                        ? 'border border-zinc-900 bg-zinc-900 text-white'
                        : 'border border-zinc-300 bg-white text-zinc-700 hover:bg-zinc-100'
                    }`}>{s}</button>
          ))}
        </div>
      </div>

      {/* County multi-select */}
      <div>
        <label className="text-xs font-semibold uppercase tracking-wider text-zinc-500">County</label>
        <div className="mt-1 flex flex-wrap gap-1">
          {COUNTIES_OPTIONS.map((c) => (
            <button key={c} onClick={() => toggleListItem('county', c)}
                    className={`rounded-full px-3 py-1 text-xs font-semibold ${
                      (filters.county || []).includes(c)
                        ? 'border border-zinc-900 bg-zinc-900 text-white'
                        : 'border border-zinc-300 bg-white text-zinc-700 hover:bg-zinc-100'
                    }`}>{c}</button>
          ))}
        </div>
      </div>

      {/* Acreage range */}
      <RangeSlider label={`Acreage: ${filters.acreage_min} – ${filters.acreage_max}`}>
        <div className="flex gap-2">
          <input type="number" value={filters.acreage_min} onChange={(e) => updateFilter('acreage_min', Number(e.target.value))}
                 className="w-24 rounded-lg border border-zinc-300 px-2 py-1 text-sm" />
          <span className="self-center text-zinc-400">→</span>
          <input type="number" value={filters.acreage_max} onChange={(e) => updateFilter('acreage_max', Number(e.target.value))}
                 className="w-24 rounded-lg border border-zinc-300 px-2 py-1 text-sm" />
        </div>
      </RangeSlider>

      {/* Substation distance */}
      <SliderRow label={`Max substation distance: ${filters.max_substation_mi} mi`}>
        <input type="range" min="0" max="25" step="0.5" value={filters.max_substation_mi}
               onChange={(e) => updateFilter('max_substation_mi', Number(e.target.value))} className="w-full" />
      </SliderRow>

      {/* Transmission on property toggle */}
      <ToggleRow label="Transmission on property" value={filters.transmission_on_property === true}
                 onToggle={() => updateFilter('transmission_on_property',
                   filters.transmission_on_property === true ? null : true)} />

      {/* Highway distance */}
      <SliderRow label={`Max highway distance: ${filters.max_highway_mi} mi`}>
        <input type="range" min="0" max="25" step="0.5" value={filters.max_highway_mi}
               onChange={(e) => updateFilter('max_highway_mi', Number(e.target.value))} className="w-full" />
      </SliderRow>

      {/* Owner types */}
      <div>
        <label className="text-xs font-semibold uppercase tracking-wider text-zinc-500">Owner type</label>
        <div className="mt-1 flex flex-wrap gap-1">
          {OWNER_TYPES.map((ot) => (
            <button key={ot} onClick={() => toggleListItem('owner_types', ot)}
                    className={`rounded-full px-3 py-1 text-xs font-semibold ${
                      (filters.owner_types || []).includes(ot)
                        ? 'border border-zinc-900 bg-zinc-900 text-white'
                        : 'border border-zinc-300 bg-white text-zinc-700 hover:bg-zinc-100'
                    }`}>{ot}</button>
          ))}
        </div>
      </div>

      {/* Exclude gov/church */}
      <ToggleRow label="Exclude government / church / school" value={filters.exclude_gov_church}
                 onToggle={() => updateFilter('exclude_gov_church', !filters.exclude_gov_church)} />

      {/* Recent transfer */}
      <SliderRow label={`Recent transfer: ${filters.recent_transfer_months === 0 ? 'any' : `≤ ${filters.recent_transfer_months} mo`}`}>
        <input type="range" min="0" max="60" step="1" value={filters.recent_transfer_months}
               onChange={(e) => updateFilter('recent_transfer_months', Number(e.target.value))} className="w-full" />
        <p className="mt-1 text-xs italic text-zinc-500">
          Only Ellis parcels currently carry deed dates — others surface as "no info" when filter > 0.
        </p>
      </SliderRow>

      {/* Min score */}
      <SliderRow label={`Min parcel score: ${filters.min_score}`}>
        <input type="range" min="0" max="100" step="1" value={filters.min_score}
               onChange={(e) => updateFilter('min_score', Number(e.target.value))} className="w-full" />
        <p className="mt-1 text-xs italic text-zinc-500">
          0 includes parcels with NULL score (e.g. backfilled before refresh ran).
        </p>
      </SliderRow>

      {/* Pipeline stage */}
      <div>
        <label className="text-xs font-semibold uppercase tracking-wider text-zinc-500">Pipeline stage</label>
        <div className="mt-1 flex flex-wrap gap-1">
          {PIPELINE_STAGES.map((s) => (
            <button key={s} onClick={() => toggleListItem('pipeline_stage', s)}
                    className={`rounded-full px-3 py-1 text-xs font-semibold ${
                      (filters.pipeline_stage || []).includes(s)
                        ? 'border border-zinc-900 bg-zinc-900 text-white'
                        : 'border border-zinc-300 bg-white text-zinc-700 hover:bg-zinc-100'
                    }`}>{s}</button>
          ))}
        </div>
      </div>

      {/* Zoning contains */}
      <SliderRow label="Zoning contains">
        <input type="text" value={filters.zoning_contains} onChange={(e) => updateFilter('zoning_contains', e.target.value)}
               placeholder="e.g. AG, PD, M-1"
               className="w-full rounded-lg border border-zinc-300 px-3 py-2 text-sm" />
      </SliderRow>

      {/* Fiber operator signal */}
      <ToggleRow label="Fiber operator signal (Digital Realty / Zayo / etc.)"
                 value={filters.fiber_operator_signal === true}
                 onToggle={() => updateFilter('fiber_operator_signal',
                   filters.fiber_operator_signal === true ? null : true)} />

      {/* FEMA flood zone — Brian's SA Corridor sprint (2026-05-09) */}
      <SliderRow label="FEMA flood zone">
        <select value={filters.flood_zone_filter || 'all'}
                onChange={(e) => updateFilter('flood_zone_filter', e.target.value)}
                className="w-full rounded-lg border border-zinc-300 bg-white px-3 py-2 text-sm">
          <option value="all">All zones (default)</option>
          <option value="X_only">Zone X only (minimal hazard)</option>
          <option value="X_or_A">Zone X + Zone A (100-yr floodplain ok)</option>
        </select>
        <p className="mt-1 text-xs italic text-zinc-500">
          NULL flood zones (not yet enriched) pass through any filter.
        </p>
      </SliderRow>
    </div>
  );
}

function SliderRow({ label, children }) {
  return (
    <div>
      <label className="text-xs font-semibold uppercase tracking-wider text-zinc-500">{label}</label>
      <div className="mt-1">{children}</div>
    </div>
  );
}

const RangeSlider = SliderRow;

function ToggleRow({ label, value, onToggle }) {
  return (
    <div className="flex items-center justify-between rounded-lg border border-zinc-200 bg-zinc-50 px-3 py-2">
      <label className="text-xs font-semibold text-zinc-700">{label}</label>
      <button onClick={onToggle}
              className={`relative inline-flex h-5 w-10 items-center rounded-full transition-colors ${
                value ? 'bg-emerald-500' : 'bg-zinc-300'
              }`}>
        <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
          value ? 'translate-x-5' : 'translate-x-1'
        }`} />
      </button>
    </div>
  );
}

function ResultsTable({ results }) {
  if (results.length === 0) {
    return (
      <div className="rounded-2xl border border-dashed border-zinc-300 bg-white p-12 text-center text-sm text-zinc-500">
        No parcels match the current filters.
      </div>
    );
  }
  return (
    <div className="overflow-x-auto rounded-2xl border border-zinc-200 bg-white">
      <table className="w-full text-sm">
        <thead className="bg-zinc-50 text-xs uppercase tracking-wider text-zinc-500">
          <tr>
            <th className="px-3 py-2 text-left">Parcel</th>
            <th className="px-3 py-2 text-left">County</th>
            <th className="px-3 py-2 text-right">Acres</th>
            <th className="px-3 py-2 text-left">Owner</th>
            <th className="px-3 py-2 text-right">Sub. dist (mi)</th>
            <th className="px-3 py-2 text-center">On-prop xmsn</th>
            <th className="px-3 py-2 text-right">Hwy dist (mi)</th>
            <th className="px-3 py-2 text-right">Score</th>
            <th className="px-3 py-2 text-left">Flood zone</th>
            <th className="px-3 py-2 text-left">Stage</th>
            <th className="px-3 py-2 text-left">Last xfer</th>
          </tr>
        </thead>
        <tbody>
          {results.map((r) => (
            <tr key={r.parcel_id} className="border-t border-zinc-200 hover:bg-zinc-50">
              <td className="px-3 py-2">
                <Link to={`/land-intelligence/parcel/${encodeURIComponent(r.parcel_id)}`}
                      className="font-mono text-xs font-bold text-blue-700 hover:underline">{r.parcel_id}</Link>
              </td>
              <td className="px-3 py-2 text-zinc-700">{r.county}, {r.state}</td>
              <td className="px-3 py-2 text-right text-zinc-700">{r.acreage?.toFixed?.(0) ?? '—'}</td>
              <td className="px-3 py-2 text-zinc-700">
                <span className="block max-w-xs truncate" title={r.owner_name || ''}>{r.owner_name || '—'}</span>
                <span className="block text-xs text-zinc-500">{r.owner_type}</span>
              </td>
              <td className="px-3 py-2 text-right font-mono text-xs">{r.nearest_substation_distance_mi?.toFixed?.(1) ?? '—'}</td>
              <td className="px-3 py-2 text-center">
                {r.transmission_on_property ? (
                  <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-semibold text-emerald-800">
                    {r.transmission_voltage_kv ? `${r.transmission_voltage_kv} kV` : 'yes'}
                  </span>
                ) : '—'}
              </td>
              <td className="px-3 py-2 text-right font-mono text-xs">{r.nearest_highway_distance_mi?.toFixed?.(1) ?? '—'}</td>
              <td className="px-3 py-2 text-right font-mono text-xs">{r.composite_score?.toFixed?.(0) ?? '—'}</td>
              <td className="px-3 py-2 text-xs">
                {r.flood_zone ? (
                  <span className={
                    String(r.flood_zone).toUpperCase().startsWith('V')
                      ? 'rounded-full bg-red-100 px-2 py-0.5 font-semibold text-red-800'
                      : String(r.flood_zone).toUpperCase().startsWith('A')
                        ? 'rounded-full bg-yellow-100 px-2 py-0.5 font-semibold text-yellow-900'
                        : 'rounded-full bg-emerald-100 px-2 py-0.5 font-semibold text-emerald-800'
                  }>{r.flood_zone}</span>
                ) : (
                  <span className="text-zinc-400 italic">unknown</span>
                )}
              </td>
              <td className="px-3 py-2 text-xs">{r.pipeline_stage}</td>
              <td className="px-3 py-2 text-xs text-zinc-500">{r.last_transfer_date || '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// Substation diamond icon (SVG data URL — small, stays in CSS)
const _SUB_ICON = L.divIcon({
  className: 'substation-marker',
  html: '<div style="width:10px;height:10px;background:#0ea5e9;border:1.5px solid #fff;transform:rotate(45deg);box-shadow:0 0 0 1px #0c4a6e"></div>',
  iconSize: [10, 10],
  iconAnchor: [5, 5],
});


// Helper that imperatively fits the map to a list of parcels.
function FitBounds({ results }) {
  const map = useMap();
  useEffect(() => {
    const valid = results.filter((r) => r.latitude != null && r.longitude != null);
    if (valid.length === 0) return;
    const bounds = L.latLngBounds(valid.map((r) => [r.latitude, r.longitude]));
    if (bounds.isValid()) {
      map.fitBounds(bounds.pad(0.2), { maxZoom: 11, animate: false });
    }
  }, [results, map]);
  return null;
}


// Map of (state, county) → infrastructure overlays cached on the
// backend via /api/land/map/cache-overlays.
function CountyOverlays({ countyKeys, authHeaders }) {
  const [overlaysByKey, setOverlaysByKey] = useState({});

  useEffect(() => {
    let cancelled = false;
    async function load() {
      const next = {};
      for (const key of countyKeys) {
        if (next[key] !== undefined) continue;
        const [state, county] = key.split('|');
        try {
          const resp = await fetch(
            `${API_BASE}/land/map/overlays?state=${encodeURIComponent(state)}&county=${encodeURIComponent(county)}&layers=transmission&layers=substations`,
            { headers: authHeaders },
          );
          if (!resp.ok) { next[key] = null; continue; }
          next[key] = await resp.json();
        } catch {
          next[key] = null;
        }
      }
      if (!cancelled) setOverlaysByKey((prev) => ({ ...prev, ...next }));
    }
    load();
    return () => { cancelled = true; };
  }, [countyKeys, authHeaders]);

  const transmissions = [];
  const substations = [];
  for (const key of countyKeys) {
    const ov = overlaysByKey[key];
    if (!ov) continue;
    const t = ov.overlays?.transmission?.features || [];
    const s = ov.overlays?.substations?.features || [];
    for (const f of t) {
      const coords = f.geometry?.coordinates || [];
      if (f.geometry?.type === 'LineString' && coords.length >= 2) {
        const latLngs = coords.map(([lon, lat]) => [lat, lon]);
        transmissions.push({
          id: f.properties?._osm_id || `${key}-${transmissions.length}`,
          latLngs,
          voltage: f.properties?.voltage,
          operator: f.properties?.operator,
        });
      }
    }
    for (const f of s) {
      const c = f.geometry?.coordinates;
      if (f.geometry?.type === 'Point' && c && c.length === 2) {
        substations.push({
          id: f.properties?._osm_id || `${key}-${substations.length}`,
          lat: c[1], lon: c[0],
          name: f.properties?.name,
          voltage: f.properties?.voltage,
          operator: f.properties?.operator,
        });
      }
    }
  }
  return (
    <>
      {transmissions.map((t) => (
        <Polyline key={`t-${t.id}`} positions={t.latLngs}
                  pathOptions={{ color: '#f97316', weight: 2.5, opacity: 0.85 }}>
          <Popup>
            <div className="text-xs">
              <div className="font-semibold">Transmission line</div>
              {t.voltage && <div>Voltage: {t.voltage}</div>}
              {t.operator && <div>Operator: {t.operator}</div>}
            </div>
          </Popup>
        </Polyline>
      ))}
      {substations.map((s) => (
        <Marker key={`s-${s.id}`} position={[s.lat, s.lon]} icon={_SUB_ICON}>
          <Popup>
            <div className="text-xs">
              <div className="font-semibold">Substation{s.name ? `: ${s.name}` : ''}</div>
              {s.voltage && <div>Voltage: {s.voltage}</div>}
              {s.operator && <div>Operator: {s.operator}</div>}
            </div>
          </Popup>
        </Marker>
      ))}
    </>
  );
}


function ResultsMap({ results }) {
  const [showOverlays, setShowOverlays] = useState(true);

  // Distinct counties present in the filtered set — drives which
  // overlay caches to fetch.
  const countyKeys = useMemo(() => {
    const keys = new Set();
    for (const r of results) {
      if (r.state && r.county) keys.add(`${r.state}|${r.county}`);
    }
    return Array.from(keys);
  }, [results]);

  // Center for the initial render — first valid parcel coord, or a
  // CONUS-center fallback if results haven't loaded.
  const initialCenter = useMemo(() => {
    const valid = results.find((r) => r.latitude != null && r.longitude != null);
    return valid ? [valid.latitude, valid.longitude] : [37.0, -95.0];
  }, [results]);

  if (results.length === 0) {
    return (
      <div className="rounded-2xl border border-dashed border-zinc-300 bg-white p-12 text-center text-sm text-zinc-500">
        No parcels to plot.
      </div>
    );
  }

  return (
    <div className="rounded-2xl border border-zinc-200 bg-white p-4">
      <div className="mb-2 flex items-center justify-between text-xs">
        <label className="flex items-center gap-2 text-zinc-700">
          <input type="checkbox" checked={showOverlays} onChange={(e) => setShowOverlays(e.target.checked)} />
          Show transmission + substation overlays ({countyKeys.length} cached counties)
        </label>
        <span className="text-zinc-500">
          {results.length} parcel{results.length === 1 ? '' : 's'} on map
        </span>
      </div>

      <div className="relative" style={{ height: '70vh', minHeight: 520 }}>
        <MapContainer
          center={initialCenter}
          zoom={6}
          scrollWheelZoom
          style={{ height: '100%', width: '100%', borderRadius: '0.5rem' }}
        >
          <FitBounds results={results} />
          <LayersControl position="topright">
            <LayersControl.BaseLayer checked name="OpenStreetMap">
              <TileLayer
                attribution='&copy; <a href="https://osm.org/copyright">OpenStreetMap</a>'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                maxZoom={19}
              />
            </LayersControl.BaseLayer>
            <LayersControl.BaseLayer name="Satellite (Esri)">
              <TileLayer
                attribution='Tiles &copy; Esri'
                url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
                maxZoom={19}
              />
            </LayersControl.BaseLayer>
          </LayersControl>

          {/* Infrastructure overlays */}
          {showOverlays && (
            <CountyOverlays countyKeys={countyKeys} authHeaders={authHeaders()} />
          )}

          {/* Parcel markers */}
          {results.map((r) => {
            if (r.latitude == null || r.longitude == null) return null;
            const color = SCORE_COLORS(r.composite_score);
            const ringColor = floodZoneRing(r.flood_zone);
            const baseR = r.transmission_on_property ? 9 : 6;
            return (
              <React.Fragment key={r.parcel_id}>
                {/* Flood-zone ring (drawn first so the score circle sits on top) */}
                {ringColor && (
                  <CircleMarker
                    center={[r.latitude, r.longitude]}
                    radius={baseR + 4}
                    pathOptions={{
                      fill: false, color: ringColor, weight: 2, opacity: 0.9,
                    }}
                  />
                )}
                <CircleMarker
                  center={[r.latitude, r.longitude]}
                  radius={baseR}
                  pathOptions={{
                    color: '#fff', weight: 1.5,
                    fillColor: color, fillOpacity: 0.85,
                  }}
                >
                  <Popup>
                    <div className="text-xs" style={{ minWidth: 220 }}>
                      <div className="mb-1 flex items-baseline justify-between gap-2">
                        <Link
                          to={`/land-intelligence/parcel/${encodeURIComponent(r.parcel_id)}`}
                          className="font-mono text-xs font-bold text-blue-700 hover:underline"
                        >{r.parcel_id}</Link>
                        <span className="rounded-full bg-zinc-100 px-2 py-0.5 text-[10px] font-semibold text-zinc-700">
                          {r.pipeline_stage}
                        </span>
                      </div>
                      <div className="text-zinc-700">
                        {r.county}, {r.state} · {r.acreage?.toFixed(0)} ac
                      </div>
                      <div className="mt-1 text-zinc-500">
                        {r.owner_name || '—'}
                        {r.owner_type && (
                          <span className="ml-1 text-[10px] uppercase tracking-wider">[{r.owner_type}]</span>
                        )}
                      </div>
                      <dl className="mt-2 grid grid-cols-2 gap-x-2 gap-y-0.5">
                        <dt className="text-zinc-500">Substation</dt>
                        <dd className="text-right font-mono">
                          {r.transmission_on_property
                            ? <span className="text-emerald-700 font-bold">on-property</span>
                            : (r.nearest_substation_distance_mi?.toFixed(1) ?? '—') + ' mi'}
                        </dd>
                        <dt className="text-zinc-500">Highway</dt>
                        <dd className="text-right font-mono">{r.nearest_highway_distance_mi?.toFixed(1) ?? '—'} mi</dd>
                        <dt className="text-zinc-500">Score</dt>
                        <dd className="text-right font-mono">{r.composite_score?.toFixed(0) ?? '—'}</dd>
                        {r.flood_zone && (
                          <>
                            <dt className="text-zinc-500">Flood zone</dt>
                            <dd className={`text-right font-mono text-[11px] ${
                              String(r.flood_zone).toUpperCase().startsWith('V')
                                ? 'text-red-700 font-bold'
                                : String(r.flood_zone).toUpperCase().startsWith('A')
                                  ? 'text-yellow-800 font-bold'
                                  : 'text-emerald-700'
                            }`}>{r.flood_zone}</dd>
                          </>
                        )}
                        {r.last_transfer_date && (
                          <>
                            <dt className="text-zinc-500">Last xfer</dt>
                            <dd className="text-right font-mono text-[11px]">{r.last_transfer_date}</dd>
                          </>
                        )}
                      </dl>
                      <Link
                        to={`/land-intelligence/parcel/${encodeURIComponent(r.parcel_id)}`}
                        className="mt-2 block rounded-lg border border-zinc-300 bg-zinc-50 py-1 text-center font-semibold text-zinc-700 hover:bg-zinc-100"
                      >
                        Open parcel detail →
                      </Link>
                    </div>
                  </Popup>
                </CircleMarker>
              </React.Fragment>
            );
          })}
        </MapContainer>
      </div>

      {/* Legend */}
      <div className="mt-3 flex flex-wrap items-center gap-4 text-xs text-zinc-600">
        <span className="flex items-center gap-1"><span className="inline-block h-3 w-3 rounded-full bg-emerald-500" /> score ≥ 60</span>
        <span className="flex items-center gap-1"><span className="inline-block h-3 w-3 rounded-full bg-amber-500" /> 30–60</span>
        <span className="flex items-center gap-1"><span className="inline-block h-3 w-3 rounded-full bg-red-500" /> &lt; 30</span>
        <span className="flex items-center gap-1"><span className="inline-block h-3 w-3 rounded-full bg-zinc-400" /> no score</span>
        <span className="ml-2 italic">Larger pin = transmission on property</span>
        <span className="flex items-center gap-1"><span className="inline-block h-3 w-3 rounded-full border-2 border-yellow-500" /> Zone A</span>
        <span className="flex items-center gap-1"><span className="inline-block h-3 w-3 rounded-full border-2 border-red-600" /> Zone V</span>
        <span className="flex items-center gap-1"><span className="inline-block h-2 w-2 rotate-45 bg-sky-500" /> Substation</span>
        <span className="flex items-center gap-1"><span className="inline-block h-1 w-4 bg-orange-500" /> Transmission</span>
        <span className="ml-auto text-zinc-500 italic">
          Tiles © OpenStreetMap. Cache overlays: <code>POST /api/land/map/cache-overlays</code>
        </span>
      </div>
    </div>
  );
}
